import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { getChangeableGmbFields } from '../../../location-utils';
import { BulkActionPopupComponent, BulkActionPopupData, SelectedFieldsRadioButton } from '../bulk-action-popup/bulk-action-popup.component';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatIcon } from '@angular/material/icon';

interface BlacklistFieldsDialogData extends BulkActionPopupData {
  selectedLocationsCount: number;
  blacklistedFieldsInfo: Map<string, string[]>;
}

@Component({
    selector: 'app-blacklist-fields-popup',
    templateUrl: './blacklist-fields-popup.component.html',
    styleUrls: ['../bulk-action-popup/bulk-action-popup.component.scss',
      'blacklist-fields-popup.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatDialogTitle,
        MatIcon,
        MatDialogContent,
        MatRadioGroup,
        MatRadioButton,
        MatCheckbox,
        NgClass,
        MatTooltip,
        MatDialogActions,
        MatButton,
        MatDialogClose,
        TranslateModule,
    ],
})
export class BlacklistFieldsPopupComponent extends BulkActionPopupComponent implements OnInit {

  protected override readonly selectedFieldsRadios: SelectedFieldsRadioButton[] = [
    SelectedFieldsRadioButton.ALL,
    SelectedFieldsRadioButton.VISIBLE_ONLY,
    SelectedFieldsRadioButton.NONE,
  ];

  constructor(
      public dialogRef: MatDialogRef<BlacklistFieldsPopupComponent>,
      @Inject(MAT_DIALOG_DATA) public data: BlacklistFieldsDialogData,
      protected fb: FormBuilder,
  ) {
    super(data, dialogRef, fb);
  }

  /**
   * Get field group tooltip
   * @param fieldID
   */
  protected getFieldGroupTooltip(fieldID: string): string | null {
    const fieldBlacklistedBy = this.data.blacklistedFieldsInfo.get(fieldID);

    if (this.isIndeterminate(fieldID)) {
      return 'Already blacklisted for: ' + (fieldBlacklistedBy.length > 10 ?
          fieldBlacklistedBy.slice(0, 10).join(', ') + ` (+${fieldBlacklistedBy.length - 10} more)` :
          fieldBlacklistedBy.join(', '));
    } else return null;
  }

  /**
   * A field is considered to be in the indeterminate state if the control is pristine && the
   * given field ID is already blacklisted but not for every selected location
   * @param fieldID
   */
  protected isIndeterminate(fieldID): boolean {
    const isPristine = this.getSelectedFieldsFormArray().controls
        [this.getSelectableFieldIndexByFieldID(fieldID)]?.pristine;
    const fieldBlacklistedBy = this.data.blacklistedFieldsInfo.get(fieldID);

    return fieldBlacklistedBy.length > 0 &&
        fieldBlacklistedBy.length < this.data.selectedLocationsCount &&
        isPristine;
  }

  /**
   * Reset form to its initial value
   */
  protected resetForm() {
    this.getSelectedFieldsFormArray().controls.forEach((fc, i) => {
      fc.setValue(this.getInitialFieldValue(this.getSelectableFieldIDByIndex(i)));
      fc.markAsPristine();
    });
  }

  /**
   * Get initial FC value
   * @param fieldID
   */
  private getInitialFieldValue(fieldID): boolean {
    const isBlacklistedBy = this.data.blacklistedFieldsInfo.get(fieldID);
    return isBlacklistedBy.length && isBlacklistedBy.length === this.data.selectedLocationsCount;
  }

  /************************************************************************************************/
  /*                                         OVERRIDES                                            */

  /************************************************************************************************/

  override ngOnInit() {
    this.getSelectableFields = getChangeableGmbFields; // init method
    super.ngOnInit();
    // required as we're overriding initSelectedFieldsFormControls()
    this.updateSelectedFieldsRadioValue();
  }

  protected override generateResult() {
    let fieldsToBeBlacklisted: string[] = [];
    let fieldsToBeWhitelisted: string[] = [];

    this.getSelectedFieldsFormArray().controls.forEach((fc, i) => {
      const fieldID = this.getSelectableFieldIDByIndex(i);
      if (fieldID && !this.isIndeterminate(fieldID) && !fc.pristine) {
        if (fc.value) {
          fieldsToBeBlacklisted.push(fieldID);
        } else fieldsToBeWhitelisted.push(fieldID);
      }
    });

    return {
      fieldsToBeBlacklisted: fieldsToBeBlacklisted,
      fieldsToBeWhitelisted: fieldsToBeWhitelisted,
    };
  }

  protected override initSelectedFieldsFormControls(): FormControl<boolean>[] {
    return this.getSelectableFields().map((fieldID) =>
        new FormControl<boolean>(this.getInitialFieldValue(fieldID)));
  }
}
